/* eslint-disable react/jsx-no-useless-fragment */
import { CategoryItemsProps, Template } from '@zeus/shared';
import { BoxComponent } from '../../../../../base/box/Box';
import { GridComponent } from '../../../../../base/grid/Grid';
import { TypographyComponent } from '../../../../../base/typography/Typography';
import { memo } from 'react';
import OutOfStock from '../OutOfStock';
import { getScaleSize, isBetweenRange } from '../../../../../utils/utils';
import { TEMPLATE_2_MODIFIERS } from '../../../../../utils/consts';
import { useNormalizedPixels } from '../../../../../hooks/useResize';

interface Item extends CategoryItemsProps {
  hasValidModifiers: boolean;
}

interface ItemProps {
  template: Template;
  item: Item;
  lastItemIndex: number;
  index?: number;
  development?: boolean;
  timezone?: string;
  scale?: number;
  hasAnyModifier: boolean;
  fontFamily?: string;
  categoryId: number;
}

const Item = ({
  template,
  item,
  lastItemIndex,
  index,
  development,
  timezone,
  scale = 1,
  hasAnyModifier,
  fontFamily: fontFamilyProp,
  categoryId,
}: ItemProps) => {
  const { normalizePixels } = useNormalizedPixels();

  const getMaxWidth = () => {
    if (isItemPriceEnabled) {
      if (item?.outOfStock) {
        return item?.hasValidModifiers ? '55%' : '65%';
      } else {
        return '100%';
      }
    } else {
      if (item?.outOfStock) {
        return '80%';
      } else {
        return '100%';
      }
    }
  };

  const isDevelopment = development ? true : isBetweenRange(item.schedules, timezone);
  const isItemPriceEnabled = template?.itemPrice?.isEnabled;
  const isDescription = template?.itemDescription?.isEnabled;
  const media = template?.media?.mediaContent;
  const isMedia = template.media?.isEnabled && media?.url;
  const templateLayout1 = template.layoutType === 1;
  const fontFamily = fontFamilyProp || template?.fontFamilyName;
  const itemId = `item-${item.id}-category-${categoryId}`;

  const style = {
    itemPrice: {
      textAlign: 'center' as const,
      fontFamily: fontFamily,
    },
    itemDesc: {
      display: '-webkit-box',
      '-webkit-line-clamp': '3',
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
      fontFamily: fontFamily,
    },
  };

  return (
    <>
      {isDevelopment && (
        <BoxComponent
          id={itemId}
          sx={{
            alignSelf: 'start',
            width: isMedia && templateLayout1 ? '33%' : '24%',
            height: 'min-content',
            overflow: 'hidden',
            paddingX: getScaleSize(normalizePixels(18, 'width'), scale),
            ...(lastItemIndex === index
              ? {
                  marginBottom: getScaleSize(normalizePixels(14, 'height'), scale),
                }
              : {}),
          }}
        >
          <BoxComponent
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              ...(isDescription && item.itemDescription
                ? {
                    marginBottom: getScaleSize(normalizePixels(6, 'height'), scale),
                  }
                : {}),
            }}
          >
            <BoxComponent
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: getScaleSize(normalizePixels(5, 'width'), scale),
                width: isItemPriceEnabled ? (item?.hasValidModifiers ? '60%' : '80%') : '100%',
              }}
            >
              <TypographyComponent
                sx={{
                  fontWeight: template.item.itemName.fontWeight,
                  fontFamily: fontFamily,
                  '-webkit-line-clamp': '1',
                  '-webkit-box-orient': 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: template.item.itemName.lineHeight,
                  fontStyle: template.item.itemName.fontStyle,
                  letterSpacing: template.item.itemName.letterSpacing,
                  color: template.item.itemName.color ? template.item.itemName.color : '#3C3333',
                  minWidth: item?.outOfStock ? 'auto' : '100%',
                  maxWidth: getMaxWidth(),
                  fontSize: getScaleSize(
                    normalizePixels(22.5 * Number(template?.item?.textScale ?? 1), 'height'),
                    scale
                  ),
                  ...(item.outOfStock ? { opacity: '30%' } : {}),
                }}
              >
                {item.itemName}
              </TypographyComponent>
              {item?.outOfStock && <OutOfStock scale={scale} fontFamily={fontFamily} />}
            </BoxComponent>

            {isItemPriceEnabled && item?.hasValidModifiers && (
              <BoxComponent width={'40%'}>
                {item.hasValidModifiers && (
                  <GridComponent container>
                    {TEMPLATE_2_MODIFIERS.map((size) => (
                      <GridComponent key={size} item xs={6}>
                        <TypographyComponent
                          sx={{
                            fontSize: getScaleSize(
                              normalizePixels(22.5 * Number(template?.item?.textScale ?? 1), 'height'),
                              scale
                            ),
                            fontWeight: template.item.itemPrice.fontWeight,
                            fontStyle: template.item.itemPrice.fontStyle,
                            letterSpacing: template.item.itemPrice.letterSpacing,
                            lineHeight: template.item.itemPrice.lineHeight,
                            color: template.item?.itemPrice?.color ? template.item?.itemPrice?.color : '#3C3333',
                            ...style.itemPrice,
                            ...(item.outOfStock ? { opacity: '30%' } : {}),
                          }}
                        >
                          {item.modifiers && item?.modifiers[size] ? Number(item?.modifiers[size]).toFixed(2) : '-'}
                        </TypographyComponent>
                      </GridComponent>
                    ))}
                  </GridComponent>
                )}
              </BoxComponent>
            )}

            {(!hasAnyModifier || !item.hasValidModifiers) && isItemPriceEnabled && (
              <TypographyComponent
                sx={{
                  // width: '20%',
                  fontSize: getScaleSize(
                    normalizePixels(22.5 * Number(template?.item?.textScale ?? 1), 'height'),
                    scale
                  ),
                  color: template?.item?.itemPrice?.color ? template?.item?.itemPrice?.color : '#3C3333',
                  fontWeight: template.item.itemPrice.fontWeight,
                  fontStyle: template.item.itemPrice.fontStyle,
                  letterSpacing: template.item.itemPrice.letterSpacing,
                  lineHeight: template.item.itemPrice.lineHeight,
                  fontFamily: fontFamily,
                  ...(item.outOfStock ? { opacity: '30%' } : {}),
                }}
              >
                {Number(item.itemPrice).toFixed(2)}
              </TypographyComponent>
            )}
          </BoxComponent>

          {template?.itemDescription && item.itemDescription && template?.itemDescription.isEnabled && (
            <TypographyComponent
              sx={{
                fontWeight: template.item.itemDescription?.fontWeight,
                fontStyle: template.item.itemDescription?.fontStyle,
                letterSpacing: template.item.itemDescription?.letterSpacing,
                lineHeight: template.item.itemDescription?.lineHeight,
                color: template.item?.itemDescription?.color ? template.item?.itemDescription?.color : '#3C3333',
                width: isItemPriceEnabled ? (item?.hasValidModifiers ? '60%' : '80%') : '100%',
                fontSize: getScaleSize(normalizePixels(16 * Number(template?.item?.textScale ?? 1), 'height'), scale),
                ...style.itemDesc,
                ...(item.outOfStock ? { opacity: '30%' } : {}),
              }}
            >
              {item.itemDescription}
            </TypographyComponent>
          )}
        </BoxComponent>
      )}
    </>
  );
};

export default memo(Item);

// Note: style object is now created dynamically in the component to use template fontFamily
